import { ChangeEvent, ClipboardEvent, KeyboardEvent, MouseEvent, useEffect, useRef } from 'react'
import { ICodeVerificationInputProps } from '../ContractSigningPageProps'
import { TVerificationCodeInputsNamesModel } from '../../../store/types'


/**
 * Hook obsługujący zbiorczą logikę pojedynczego pola do wpisywania kodu sms
*/
const useCodeVerificationInputLogic = (props: ICodeVerificationInputProps)  => {
   const DATASET_INPUT_IDENTIFIER = 'code-verification-input'
   const inputsRef = useRef<NodeListOf<HTMLInputElement> | []>([])
   const onlyDigitPattern = /\D/

   // >>>>> USE EFFECT
   useEffect(() => {
      inputsRef.current = document.querySelectorAll(`input[input-identifier=${DATASET_INPUT_IDENTIFIER}]`);
   }, [])


    // >>>>> HANDLERS
   const handleOnClick = (e: MouseEvent<HTMLInputElement>) => { //Ustawianie kursora zawsze na początku tekstu po kliknięciu
      const input = e.currentTarget;
      const end = input?.value?.length ?? null
      input?.setSelectionRange(end, end)
   }

   const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => { 
      const inputId = parseInt(e.currentTarget.id)
      const prevInput = document.getElementById((inputId - 1).toString()) as HTMLInputElement
      const nextInput = document.getElementById((inputId + 1).toString()) as HTMLInputElement


      switch(e.key){
         case 'Backspace': {
            if(props.value.trim().length){
               props.handleSaveInputValueToState(props.name, '')
            }
            else if(prevInput){
               prevInput.focus()
               props.handleSaveInputValueToState(prevInput.name as TVerificationCodeInputsNamesModel, '')
            }

            break;
         }
         case 'Delete': {
            if(props.value.trim().length){
               props.handleSaveInputValueToState(props.name, '')
            }
            else if(nextInput && !props.value.trim().length){
               nextInput.focus()
               props.handleSaveInputValueToState(nextInput.name as TVerificationCodeInputsNamesModel, '')
            }

            break;
         }
         case 'ArrowRight': {// Ustawiamy kursor na NASTĘPNYM polu
            e.currentTarget?.setSelectionRange(1,1) // Ustawiamy "kursor" przed tekstem pozwalając na kosowanie tekstu w kontrolce w której obecnie się znajdujemy
            if(nextInput){
               e.preventDefault()
               nextInput.focus();
            }

            break;
         }
         case 'ArrowLeft': {// Ustawiamy kursor na POPRZEDNIM polu
            e.currentTarget?.setSelectionRange(0,0) // Ustawiamy "kursor" przed tekstem pozwalając na kosowanie tekstu w kontrolce w której obecnie się znajdujemy
            if(prevInput){
               e.preventDefault()
               prevInput.focus();
            }

            break;
         }
         case 'ArrowDown': {// Ustawiamy kursor na PIERWSZYM polu
            e.currentTarget?.setSelectionRange(0,0) // Ustawiamy "kursor" przed tekstem pozwalając na kosowanie tekstu w kontrolce w której obecnie się znajdujemy
            const firstInput = inputsRef.current[0]
           
            if(firstInput){
               e.preventDefault()
               firstInput.focus()
            }

            break;
         }
         case 'ArrowUp': { // Ustawiamy kursor na OSTATNIM polu
            e.currentTarget?.setSelectionRange(0,0) // Ustawiamy "kursor" przed tekstem pozwalając na kosowanie tekstu w kontrolce w której obecnie się znajdujemy
            const lastInput = inputsRef.current[inputsRef.current.length - 1]
 
            if(lastInput){
               e.preventDefault()
               lastInput.focus()
            }

            break;
         }
         default: {
            return
         }
      }
   }

   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const inputId = parseInt(e.currentTarget.id)
      
      if(onlyDigitPattern.test(e.currentTarget.value)){
         return
      }
      else {
         if(inputsRef.current.length  !== inputId){ //Wyklucza ostatniego inputa
            const nextInput = document.getElementById((inputId + 1).toString())

            // Focus na następne pole ustawiamy tylko gdy pole nie ma jeszcze wartości
            if (nextInput) {
               nextInput.focus()
            }
         }
      }

      props.handleSaveInputValueToState(props.name, e.currentTarget.value)
   }

   const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const pasedText = e.clipboardData.getData('text') //Pobieramy skopiowaną wartość ze schowka

      if(onlyDigitPattern.test(pasedText)){ //Jeśli wklejony tekst nie jest liczbą - nic nie robimy
         return
      }
      
      props.resetForm() //Przed każdym wklejeniem wartości czyścimy wartości pól

      const currentIndex = parseInt(e.currentTarget.id)
      let charIndex = 0 
      const lastInputIndex = currentIndex + pasedText.length - 1 // Wyliczanie indexu ostatniego pola w którym znajduje się wartość po wklejeniu tekstu 

      for(let i = currentIndex; i <= inputsRef.current.length - 1; i++){ //Pętla od konkretnego pola "do końca" przez pozostałe pola  
         if(inputsRef.current[i]?.getAttribute('name') !== undefined) {
            const inputName = inputsRef.current[i].getAttribute('name') as TVerificationCodeInputsNamesModel
            props.handleSaveInputValueToState(inputName, pasedText.charAt(charIndex)) //Wypełniamy wszystkie pola po kolei wartością pobraną ze schowka o odpowiednim indexie  
         }
         
         charIndex = charIndex + 1 //Z każdym przebiegiem pętli "przesuwamy" index o jeden do przodu
      }

      if(lastInputIndex >= inputsRef.current.length){ //Jeśli wklejony tekst nie mieści się w kontrolce - ustawiamy focus na ostatnim polu (ostatnim "okienku" do wpisania liczby)
         const lastInput = document.getElementById((inputsRef.current.length - 1).toString()) as HTMLInputElement
         lastInput.focus()
      }
      else { //Jeśli wklejony tekst mieści się w kontrolce - ustawiamy focus na ostatnim polu posiadającym wartość
         const lastInputWithValue = document.getElementById((lastInputIndex).toString()) as HTMLInputElement
         lastInputWithValue.focus()
      } 
   }

   return {
      handleOnClick,
      handleKeyDown,
      handleChange,
      handlePaste,
      DATASET_INPUT_IDENTIFIER
   }
}

export default useCodeVerificationInputLogic